/**
 * 纯内存操作历史（不落盘）。页面刷新 / hardReload 后自然清空。
 * 仅供客户端发版升级风险判定使用。
 */

export type ActionCategory =
	| "mutate"
	| "export"
	| "purchaseExport"
	| "finance"
	| "settle"
	| "print"
	| "workOrder"
	| "customerService"
	| "express"
	| "recharge"
	| "redReversal"
	| "other";

export type ActionKind = "request" | "button";

export type ActionStatus = "pending" | "success" | "fail";

export type ActionRecord = {
	id: string;
	kind: ActionKind;
	url?: string;
	method?: string;
	label?: string;
	category: ActionCategory;
	status: ActionStatus;
	t: number;
};

const MAX_SIZE = 50;
const WINDOW_MS = 5 * 60 * 1000;

const IGNORE_SUFFIXES = ["eps", "refreshToken", "version.json"];
const IGNORE_READONLY = ["/list", "/page", "/info"];

/** 需要弹窗确认后再升级的类别（不仅限新增/修改） */
export const MODAL_RISK_CATEGORIES: ReadonlySet<ActionCategory> = new Set([
	"mutate",
	"workOrder",
	"customerService",
	"express",
	"recharge",
	"redReversal",
	"purchaseExport"
]);

let seq = 0;
const records: ActionRecord[] = [];
const listeners = new Set<() => void>();

function nextId(): string {
	return `ah-${Date.now()}-${++seq}`;
}

function normalizeUrl(url?: string): string {
	if (!url) return "";
	try {
		if (url.startsWith("http")) {
			return new URL(url).pathname.toLowerCase();
		}
	} catch {
		/* ignore */
	}
	return url.split("?")[0].toLowerCase();
}

function isExportFlag(data?: unknown): boolean {
	if (!data || typeof data !== "object") return false;
	const v = (data as Record<string, unknown>).isExport;
	return v === true || v === 1 || v === "1" || v === "true";
}

export function isModalRiskCategory(category: ActionCategory): boolean {
	return MODAL_RISK_CATEGORIES.has(category);
}

export function classifyUrl(
	url?: string,
	method?: string,
	label?: string,
	isExport = false
): ActionCategory {
	const path = normalizeUrl(url);
	const text = `${path} ${label || ""}`.toLowerCase();
	const m = (method || "").toUpperCase();

	// 购买订单导出：orders/page + body.isExport
	if (isExport && /\/orders(?:\/|$)/.test(path)) {
		return "purchaseExport";
	}

	// 客诉重打 / 重修 / 重建
	if (
		/reprint|reprintorder|modelcustomerservice|modelbugfeedback|customerserviceissue|modelrepairorder|modelrepatbuild|returnforrepair|complaintrebuild|cancelrework|客诉|重打|重修|重建/.test(
			text
		)
	) {
		return "customerService";
	}

	// 工单处理
	if (/factoryworkorder|internalworkorder|work[-_]?order|工单/.test(text)) {
		return "workOrder";
	}

	// 快递管理 / 快递打包（不含库存 packaging 物料）
	if (
		/modelexpress|expresscompany|expressfreight|financialexpress|快递|打包|面单|取号/.test(
			text
		) &&
		!/\/packaging(?:\/|$)/.test(path)
	) {
		return "express";
	}

	// 充值
	if (/rechargeorder|recharge|充值/.test(text)) {
		return "recharge";
	}

	// 红冲
	if (/redreversal|reversebill|红冲/.test(text)) {
		return "redReversal";
	}

	if (
		/\/add(?:\/|$)/.test(path) ||
		/\/update(?:\/|$)/.test(path) ||
		((m === "POST" || m === "PUT" || m === "PATCH") &&
			(path.includes("/add") || path.includes("/update")))
	) {
		return "mutate";
	}

	if (isExport || /export|isexport|导出/.test(text)) return "export";
	if (/fin|finance|financial|财务/.test(text)) return "finance";
	if (/settle|settlement|结算/.test(text)) return "settle";
	if (/print|打印/.test(text)) return "print";
	if (/\/delete(?:\/|$)/.test(path)) return "mutate";

	return "other";
}

export function shouldTrackRequest(url?: string, isExport = false): boolean {
	if (!url) return false;
	const path = normalizeUrl(url);

	if (IGNORE_SUFFIXES.some((suffix) => path.endsWith(suffix) || path.includes(`/${suffix}`))) {
		return false;
	}

	// 购买订单导出走 page + isExport，必须跟踪
	if (isExport && /\/orders(?:\/|$)/.test(path)) {
		return true;
	}

	// 高风险业务命名空间：即使是 page/list 也跟踪（避免只读操作漏判后写操作前漏记）
	// 仅跟踪非纯 info；page/list 在高风险域保留，便于按钮后紧接列表刷新仍有域信号
	const highRiskNs =
		/reprint|modelcustomerservice|modelbugfeedback|customerserviceissue|modelrepairorder|factoryworkorder|internalworkorder|modelexpress|rechargeorder|redreversal|reversebill/.test(
			path
		);

	if (
		IGNORE_READONLY.some((suffix) => path.endsWith(suffix)) &&
		!highRiskNs &&
		!/export|settle|print|fin|work|recharge|express|redreversal|reversebill|客诉/.test(path)
	) {
		return false;
	}

	return true;
}

function prune(): void {
	const cutoff = Date.now() - WINDOW_MS;
	while (records.length > 0 && (records.length > MAX_SIZE || records[0].t < cutoff)) {
		if (records.length > MAX_SIZE) {
			records.shift();
			continue;
		}
		if (records[0].t < cutoff) {
			records.shift();
			continue;
		}
		break;
	}
}

function notify(): void {
	listeners.forEach((fn) => {
		try {
			fn();
		} catch {
			/* ignore */
		}
	});
}

export function onActionHistoryChange(fn: () => void): () => void {
	listeners.add(fn);
	return () => {
		listeners.delete(fn);
	};
}

export function pushRequestStart(
	url?: string,
	method?: string,
	data?: unknown
): string | null {
	const exportFlag = isExportFlag(data);
	if (!shouldTrackRequest(url, exportFlag)) return null;

	const id = nextId();
	const category = classifyUrl(url, method, undefined, exportFlag);
	records.push({
		id,
		kind: "request",
		url,
		method,
		category,
		status: "pending",
		t: Date.now()
	});
	prune();
	notify();
	return id;
}

export function markRequestEnd(id: string | null | undefined, status: "success" | "fail"): void {
	if (!id) return;
	const item = records.find((r) => r.id === id);
	if (!item) return;
	item.status = status;
	item.t = Date.now();
	prune();
	notify();
}

export function pushButton(label?: string): void {
	const text = (label || "").trim();
	if (!text) return;

	records.push({
		id: nextId(),
		kind: "button",
		label: text.slice(0, 80),
		category: classifyUrl(undefined, undefined, text),
		status: "success",
		t: Date.now()
	});
	prune();
	notify();
}

function recent(): ActionRecord[] {
	prune();
	const cutoff = Date.now() - WINDOW_MS;
	return records.filter((r) => r.t >= cutoff);
}

/** 近期存在需弹窗确认的高风险操作 */
export function hasRiskyRecent(): boolean {
	return recent().some(
		(r) =>
			isModalRiskCategory(r.category) &&
			(r.status === "pending" || r.status === "success" || r.status === "fail")
	);
}

/** 当前有未结束的高风险请求 */
export function hasPendingMutations(): boolean {
	return recent().some(
		(r) => r.kind === "request" && isModalRiskCategory(r.category) && r.status === "pending"
	);
}

export function getActionHistorySnapshot(): ActionRecord[] {
	return recent().slice();
}

/** 仅供单测重置内存环 */
export function resetActionHistoryForTest(): void {
	records.length = 0;
	seq = 0;
}
